import { useTranslate, useList } from '@refinedev/core';
import { Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Field } from 'react-querybuilder';
import type {
  CollectionFilterGroupIn,
  FilterRule,
} from '@/api/generated/schemas';
import { useFilterFields } from './useFilterFields';
import {
  ASYNC_FIELD_RESOURCE_MAP,
  DATE_FIELDS,
  NO_VALUE_OPERATORS,
  RANGE_OPERATORS,
} from './fieldMappings';

const { Text } = Typography;

type NamedOption = { name: string; label: string };

const asOptions = (list: unknown): NamedOption[] =>
  Array.isArray(list) ? (list as NamedOption[]) : [];

const toArray = (value: unknown): (string | number)[] => {
  if (Array.isArray(value)) return value as (string | number)[];
  return value === '' || value == null ? [] : [value as string | number];
};

const formatDate = (value: unknown): string => {
  const d = dayjs(value as string);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : String(value ?? '');
};

/** Resolves async value ids/codes to human labels via the resource. */
const AsyncValueLabels = ({
  field,
  value,
}: {
  field: string;
  value: unknown;
}) => {
  const cfg = ASYNC_FIELD_RESOURCE_MAP[field]!;
  const values = toArray(value);

  const { result } = useList<Record<string, unknown>>({
    resource: cfg.resource,
    filters: [{ field: cfg.optionValue, operator: 'in', value: values }],
    pagination: { pageSize: Math.max(values.length, 1), mode: 'server' },
    queryOptions: { enabled: values.length > 0 },
  });

  const labelByValue = new Map(
    (result?.data ?? []).map((record) => [
      record[cfg.optionValue] as string | number,
      String(record[cfg.optionLabel] ?? record[cfg.optionValue]),
    ])
  );

  return (
    <>
      {values.map((v) => (
        <Tag key={String(v)}>{labelByValue.get(v) ?? String(v)}</Tag>
      ))}
    </>
  );
};

/** Renders one rule's value, resolving labels/dates by field type. */
const RuleValue = ({
  rule,
  fieldMeta,
}: {
  rule: FilterRule;
  fieldMeta?: Field;
}) => {
  if (NO_VALUE_OPERATORS.has(rule.operator ?? '')) {
    return <Text type="secondary">—</Text>;
  }

  if (ASYNC_FIELD_RESOURCE_MAP[rule.field]) {
    return <AsyncValueLabels field={rule.field} value={rule.value} />;
  }

  if (DATE_FIELDS.has(rule.field)) {
    if (RANGE_OPERATORS.has(rule.operator ?? '')) {
      const [from, to] = toArray(rule.value);
      return <Text>{`${formatDate(from)} – ${formatDate(to)}`}</Text>;
    }
    return <Text>{formatDate(rule.value)}</Text>;
  }

  // Map select option values to their labels when available.
  const values = toArray(rule.value);
  const labelFor = (v: string | number) =>
    String(
      asOptions(fieldMeta?.values).find((opt) => opt.name === String(v))
        ?.label ?? v
    );

  return <Text>{values.map(labelFor).join(', ')}</Text>;
};

interface Props {
  value?: CollectionFilterGroupIn | null;
}

/** Read-only, tabular summary of dynamic-collection filters. */
export const CollectionFilterSummary = ({ value }: Props) => {
  const t = useTranslate();
  const { fields } = useFilterFields();

  const rules = (value?.rules ?? []).filter(
    (r): r is FilterRule => 'field' in r
  );

  if (rules.length === 0) {
    return <Text type="secondary">{t('base.collection.no_filters')}</Text>;
  }

  return (
    <Table<FilterRule>
      dataSource={rules}
      rowKey={(_, index) => String(index)}
      pagination={false}
      size="small"
    >
      <Table.Column
        title={t('base.collection.rule_field')}
        render={(_, rule: FilterRule) =>
          fields.find((f) => f.name === rule.field)?.label ?? rule.field
        }
      />
      <Table.Column
        title={t('base.collection.rule_operator')}
        render={(_, rule: FilterRule) => {
          const meta = fields.find((f) => f.name === rule.field);
          const op = asOptions(meta?.operators).find(
            (o) => o.name === rule.operator
          );
          return op?.label ?? rule.operator ?? '';
        }}
      />
      <Table.Column
        title={t('base.collection.rule_value')}
        render={(_, rule: FilterRule) => (
          <RuleValue
            rule={rule}
            fieldMeta={fields.find((f) => f.name === rule.field)}
          />
        )}
      />
    </Table>
  );
};
